import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, TrendingUp, User, LogOut, Copy, AlertTriangle,
  ChevronRight, ArrowUpRight, ArrowDownRight, ExternalLink, Phone, Mail,
  Save, Eye, EyeOff, X, Download, Search, UserMinus, UserPlus, Calendar,
  Building2, FileText, CreditCard, KeyRound, Percent, Shield, ScrollText,
  CheckSquare, Square, Bot, ChevronDown, MessageCircle, BookOpen, Briefcase, Share2, ArrowRight
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { PWAInstallBanner } from "../../components/PWAInstall";
import DOMPurify from "dompurify";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════ VENDITORE APP ═══════════
export default function VenditoreApp() {
  const [token, setToken] = useState(localStorage.getItem("venditore_token"));
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [contractPending, setContractPending] = useState(false);
  const [contractData, setContractData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem("venditore_user");
    if (stored) try { setUser(JSON.parse(stored)); } catch (e) { console.warn("[VenditoreApp] parse stored user failed:", e); }
  }, []);

  // Controlla il contratto pendente quando il token è già in localStorage (page refresh)
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/venditore/contratto-pendente`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          if (d.pending && d.contratto) {
            setContractData(d.contratto);
            setContractPending(true);
          }
        }
      } catch (e) { console.warn("[VenditoreApp] token validation failed:", e); }
    })();
  }, [token]);

  const handleLogin = (tk, usr, contrattoPendente = false, contrattoData = null) => {
    localStorage.setItem("venditore_token", tk);
    localStorage.setItem("venditore_user", JSON.stringify(usr));
    setToken(tk);
    setUser(usr);
    if (contrattoPendente && contrattoData) {
      setContractData(contrattoData);
      setContractPending(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("venditore_token");
    localStorage.removeItem("venditore_user");
    setToken(null);
    setUser(null);
    navigate("/");
  };

  const handleContractAccepted = () => {
    setContractPending(false);
    setContractData(null);
  };

  if (!token) return <VenditoreLogin onLogin={handleLogin} />;

  const headers = { Authorization: `Bearer ${token}` };
  const NAV = [
    { key: "dashboard", label: "Home", icon: LayoutDashboard },
    { key: "clienti", label: "Clienti", icon: Users },
    { key: "provvigioni", label: "Provvigioni", icon: TrendingUp },
    { key: "materiali", label: "Materiali", icon: Briefcase },
    { key: "profilo", label: "Profilo", icon: User },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="venditore-app">
      {/* Modal contratto primo accesso */}
      {contractPending && contractData && (
        <ContractModal
          contratto={contractData}
          token={token}
          onAccepted={handleContractAccepted}
        />
      )}

      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/yollo-logo.png" alt="Yollo" className="h-9 w-auto" />
          <div className="h-6 w-px bg-gray-200" />
          <p className="text-base font-bold text-gray-900">{user?.nome || "Venditore"}</p>
        </div>
        <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-gray-100" data-testid="venditore-logout">
          <LogOut className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pt-4 max-w-2xl mx-auto">
        {/* PWA Install Banner */}
        <PWAInstallBanner area="venditore" />
        {page === "dashboard" && <VenditoreDashboard headers={headers} />}
        {page === "clienti" && <VenditoreClienti headers={headers} />}
        {page === "provvigioni" && <VenditoreProvvigioni headers={headers} />}
        {page === "materiali" && <VenditoreMateriali headers={headers} />}
        {page === "profilo" && <VenditoreProfilo headers={headers} />}
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t z-50 flex" data-testid="venditore-nav">
        {NAV.map(n => (
          <button key={n.key} onClick={() => setPage(n.key)}
            className={`flex-1 flex flex-col items-center py-2.5 gap-0.5 transition-colors ${page === n.key ? "text-indigo-600" : "text-gray-400"}`}
            data-testid={`nav-${n.key}`}>
            <n.icon className="h-5 w-5" />
            <span className="text-[10px] font-semibold">{n.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

// ═══════════ MODAL CONTRATTO ═══════════
function ContractModal({ contratto, token, onAccepted }) {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!accepted) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/venditore/accetta-contratto`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Contratto accettato — Benvenuto in Yollo!");
        onAccepted();
      } else {
        toast.error("Errore durante l'accettazione. Riprova.");
      }
    } catch {
      toast.error("Errore di connessione.");
    }
    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center bg-slate-900/80 backdrop-blur-sm"
      data-testid="contract-modal"
    >
      <div className="w-full sm:max-w-2xl sm:mx-4 flex flex-col bg-white sm:rounded-2xl sm:shadow-2xl overflow-hidden max-h-screen">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-5 flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <ScrollText className="h-5 w-5 text-indigo-400" />
          </div>
          <div>
            <p className="text-white font-bold text-base">{contratto.titolo || "Contratto Commerciale"}</p>
            <p className="text-slate-400 text-xs mt-0.5">Leggi attentamente prima di accedere al pannello</p>
          </div>
        </div>

        {/* Body scrollabile */}
        <div
          className="flex-1 overflow-y-auto px-6 py-5"
          style={{ minHeight: 0, maxHeight: "calc(100vh - 200px)" }}
          data-testid="contract-content"
        >
          <div
            className="prose prose-sm max-w-none text-slate-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contratto.contenuto || "<p>Nessun contenuto disponibile.</p>") }}
          />
        </div>

        {/* Footer accettazione */}
        <div className="border-t border-slate-100 px-6 py-4 bg-slate-50 flex-shrink-0 space-y-3">
          <button
            type="button"
            onClick={() => setAccepted(a => !a)}
            className="flex items-start gap-3 w-full text-left"
            data-testid="contract-checkbox"
          >
            {accepted
              ? <CheckSquare className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              : <Square className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            }
            <span className="text-sm text-slate-700 font-medium">
              Ho letto e accetto le condizioni del contratto commerciale
            </span>
          </button>

          <Button
            onClick={handleAccept}
            disabled={!accepted || loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 rounded-xl font-bold text-sm gap-2"
            data-testid="contract-accept-btn"
          >
            {loading
              ? <><span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin inline-block" /> Conferma in corso...</>
              : <><Shield className="h-4 w-4" /> Conferma e accedi al pannello</>
            }
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════ DEMO CARD ═══════════
function DemoCard() {
  const [openCat, setOpenCat] = useState(null);
  const appUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/api$/, "") || window.location.origin;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato!`);
  };

  const FAQ_CATEGORIE = [
    {
      key: "personale",
      label: "Personale & Turni",
      color: "bg-indigo-100 text-indigo-700",
      domande: [
        "Chi lavora oggi a pranzo?",
        "Chi lavora stasera?",
        "Mostrami le richieste ferie in attesa",
        "Quante presenze ha fatto Marco questo mese?",
        "Dammi le presenze del mese scorso in Excel",
        "Chi è libero martedì prossimo?",
      ]
    },
    {
      key: "cassa",
      label: "Cassa & Contabilità",
      color: "bg-emerald-100 text-emerald-700",
      domande: [
        "Com'è andata ieri sera in cassa?",
        "Incassi di questo mese",
        "Spese della settimana",
        "Quant'è il saldo di cassa oggi?",
        "Qual è stato il giorno migliore del mese?",
        "Dammi le ultime registrazioni di cassa",
      ]
    },
    {
      key: "haccp",
      label: "HACCP & Magazzino",
      color: "bg-orange-100 text-orange-700",
      domande: [
        "Qual è la temperatura del frigo?",
        "Prodotti in scadenza nei prossimi 7 giorni",
        "Mostrami i lotti aperti",
        "Dammi il piano pulizie di oggi",
      ]
    },
    {
      key: "acquisti",
      label: "Acquisti & Fornitori",
      color: "bg-violet-100 text-violet-700",
      domande: [
        "Mostrami gli ultimi ordini ai fornitori",
        "Quali fatture non sono ancora pagate?",
        "Quanto ho speso da Caseificio Valloni questo mese?",
        "Qual è il fornitore con più spese?",
      ]
    },
    {
      key: "documenti",
      label: "Documenti",
      color: "bg-rose-100 text-rose-700",
      domande: [
        "Dammi il certificato medico di Maurizio",
        "Visura camerale della società",
        "Mostrami i documenti di Simona",
        "Contratto di lavoro di Maurizio",
      ]
    },
  ];

  return (
    <Card className="border-0 shadow-lg overflow-hidden rounded-3xl bg-white" data-testid="demo-card">
      <CardContent className="p-0">
        {/* Header dark */}
        <div
          className="bg-indigo-950 px-6 py-6 flex items-start gap-4 relative overflow-hidden"
          style={{ color: 'white' }}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-600/30 blur-3xl rounded-full" />
          <div className="w-12 h-12 rounded-2xl bg-indigo-800 border border-indigo-600/40 flex items-center justify-center shrink-0 relative z-10">
            <Bot className="h-6 w-6" style={{ color: '#e0e7ff' }} />
          </div>
          <div className="relative z-10 flex-1 pt-1">
            <p className="font-black text-base leading-tight mb-1" style={{ color: 'white' }}>
              Prova il Bot con i tuoi Clienti
            </p>
            <p className="text-[11px] font-medium" style={{ color: 'rgba(199,210,254,0.9)' }}>
              Demo live — Trattoria Romana &amp; Bistrot Milanese
            </p>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* 3 passi */}
          <div className="space-y-4">
            {[
              { n: "1", text: "Apri @YolloDemoBot su Telegram" },
              { n: "2", text: <>Inserisci la password: <code className="bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-md px-1.5 py-0.5 font-mono text-[11px] font-bold">yollo@2026</code></> },
              { n: "3", text: "Inizia a fare domande al bot!" },
            ].map(({ n, text }) => (
              <div key={n} className="flex items-start gap-4">
                <div className="w-7 h-7 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center shrink-0 mt-0.5">
                  {n}
                </div>
                <p className="text-sm font-medium text-slate-700 leading-snug pt-1">{text}</p>
              </div>
            ))}
          </div>

          {/* Bottoni Bot */}
          <div className="flex flex-col gap-3">
            <a href="https://t.me/YolloDemoBot" target="_blank" rel="noopener noreferrer" className="w-full" data-testid="demo-open-bot">
              <Button className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white gap-2 rounded-xl text-sm font-bold shadow-md shadow-indigo-600/20">
                <ExternalLink className="h-4 w-4" />Apri @YolloDemoBot
              </Button>
            </a>
            <Button variant="outline" className="w-full h-12 border-indigo-200 text-indigo-700 rounded-xl text-sm font-bold hover:bg-indigo-50"
              onClick={() => copy("yollo@2026", "Password bot")} data-testid="copy-bot-password">
              <Copy className="h-4 w-4 mr-2" />Copia password
            </Button>
          </div>

          <hr className="border-slate-100" />

          {/* Credenziali app demo — Admin */}
          <div className="space-y-4">
            <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5" />Accesso App Demo — Admin
            </p>
            <div className="space-y-2">
              {[
                { label: "Email", value: "demo@yollo.me" },
                { label: "Password", value: "demo2026" },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span className="text-xs font-semibold text-slate-500 w-16 shrink-0">{label}</span>
                  <code className="flex-1 text-slate-800 font-mono text-sm font-bold truncate">{value}</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 shrink-0 rounded-lg"
                    onClick={() => copy(value, label)} data-testid={`copy-demo-${label.toLowerCase()}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Credenziali app demo — Operatore Cucina */}
          <div className="space-y-3">
            <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5" />Accesso App Demo — Operatore Cucina
            </p>
            <div className="space-y-2">
              {[
                { label: "Email", value: "operatore@yollo.me" },
                { label: "Password", value: "demo2026" },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span className="text-xs font-semibold text-slate-500 w-16 shrink-0">{label}</span>
                  <code className="flex-1 text-slate-800 font-mono text-sm font-bold truncate">{value}</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 shrink-0 rounded-lg"
                    onClick={() => copy(value, label)} data-testid={`copy-op-${label.toLowerCase()}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <Button variant="ghost" className="w-full h-10 text-indigo-600 bg-indigo-50/50 hover:bg-indigo-100 rounded-xl text-xs font-bold gap-2"
              onClick={() => window.open(appUrl, "_blank")} data-testid="open-demo-app">
              <ExternalLink className="h-3.5 w-3.5" />Apri app demo
            </Button>
          </div>

          {/* FAQ a categorie */}
          <div className="space-y-2">
            <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <MessageCircle className="h-3.5 w-3.5" />Comandi di esempio per categoria
            </p>
            {FAQ_CATEGORIE.map(cat => (
              <div key={cat.key} className="bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden">
                <button
                  onClick={() => setOpenCat(openCat === cat.key ? null : cat.key)}
                  className="w-full flex items-center justify-between p-3.5 hover:bg-slate-100/50 transition-colors"
                  data-testid={`faq-cat-${cat.key}`}
                >
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${cat.color}`}>
                    {cat.label}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${openCat === cat.key ? "rotate-180" : ""}`} />
                </button>
                {openCat === cat.key && (
                  <ul className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
                    {cat.domande.map((q, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="text-indigo-300 shrink-0 text-xs mt-1">◆</span>
                        <p className="text-xs font-medium text-slate-600 italic leading-snug">"{q}"</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════ MATERIALI VENDITA ═══════════
const COLORI_MAP = {
  indigo:  { bg: "bg-indigo-600",  light: "bg-indigo-50",  text: "text-indigo-600",  border: "border-indigo-200",  hex: "#4f46e5", shadow: "rgba(79,70,229,0.22)"   },
  emerald: { bg: "bg-emerald-600", light: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", hex: "#059669", shadow: "rgba(5,150,105,0.22)"   },
  rose:    { bg: "bg-rose-500",    light: "bg-rose-50",    text: "text-rose-600",    border: "border-rose-200",    hex: "#f43f5e", shadow: "rgba(244,63,94,0.22)"   },
  orange:  { bg: "bg-orange-500",  light: "bg-orange-50",  text: "text-orange-600",  border: "border-orange-200",  hex: "#f97316", shadow: "rgba(249,115,22,0.22)"  },
  purple:  { bg: "bg-purple-600",  light: "bg-purple-50",  text: "text-purple-600",  border: "border-purple-200",  hex: "#9333ea", shadow: "rgba(147,51,234,0.22)"  },
  amber:   { bg: "bg-amber-500",   light: "bg-amber-50",   text: "text-amber-600",   border: "border-amber-200",   hex: "#f59e0b", shadow: "rgba(245,158,11,0.22)"  },
};

function VenditoreMateriali({ headers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/venditore/materiali`, { headers });
        setItems(res.data);
      } catch { toast.error("Errore caricamento materiali"); }
      setLoading(false);
    })();
  }, []);

  const handleShare = async (item) => {
    const url = item.download_url;
    if (navigator.share) {
      try {
        await navigator.share({ title: item.titolo, text: item.descrizione || item.titolo, url });
      } catch (e) {
        if (e.name !== "AbortError") {
          try { await navigator.clipboard.writeText(url); toast.success("Link copiato!"); } catch (e) { console.warn("[VenditoreApp] clipboard write failed:", e); }
        }
      }
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Link copiato negli appunti!"); } catch (e) { console.warn("[VenditoreApp] clipboard write failed:", e); }
    }
  };

  const formatDataIta = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground text-sm">Caricamento...</div>;

  return (
    <div className="space-y-4 pb-4" data-testid="venditore-materiali">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Materiali di Vendita</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Documenti e presentazioni per le tue trattative</p>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
          <Briefcase className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">Nessun materiale disponibile</p>
          <p className="text-xs text-gray-400 mt-1">I materiali saranno resi disponibili dal team Yollo</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => {
            const c = COLORI_MAP[item.colore] || COLORI_MAP.indigo;
            return (
              <div
                key={item.id}
                className="relative p-6 rounded-3xl bg-white overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:scale-[1.01] active:scale-[0.98]"
                style={{ border: `2px solid ${c.hex}`, boxShadow: `0 24px 80px -12px ${c.shadow}` }}
                onClick={() => window.open(item.download_url, '_blank', 'noreferrer')}
                data-testid={`materiale-card-${item.id}`}
              >
                {/* Data sopra il titolo - viola app */}
                {item.created_at && (
                  <p className="text-xs font-semibold tracking-wide text-[#6C63FF] mb-2 uppercase">
                    {formatDataIta(item.created_at)}
                  </p>
                )}

                {/* Titolo - grande, non bold, compatto */}
                <h3 className="text-[1.55rem] font-medium tracking-[-0.02em] text-gray-900 leading-snug mb-2">
                  {item.titolo}
                </h3>

                {/* Descrizione */}
                {item.descrizione && (
                  <p className="text-sm text-gray-500 leading-snug">{item.descrizione}</p>
                )}

                <div className="mt-6" />

                {/* Bottone Condividi - allineato a destra, grigio + pallino nero freccia */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleShare(item); }}
                    className="flex items-center gap-3 bg-gray-100 rounded-full pl-5 pr-1.5 py-1.5 hover:bg-gray-200 transition-colors"
                    data-testid={`condividi-materiale-${item.id}`}
                  >
                    <span className="text-sm font-semibold text-gray-800">Condividi</span>
                    <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                      <ArrowRight strokeWidth={3} color="white" className="h-4 w-4" />
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ═══════════ LOGIN ═══════════
function VenditoreLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/venditore/auth/login`, { email, password });
      const d = res.data;
      let contrattoData = null;
      if (d.contratto_pendente) {
        // Fetch the contract content
        try {
          const cr = await fetch(`${API}/api/venditore/contratto-pendente`, {
            headers: { Authorization: `Bearer ${d.access_token}` }
          });
          if (cr.ok) {
            const cd = await cr.json();
            if (cd.pending) contrattoData = cd.contratto;
          }
        } catch (e) { console.warn("[VenditoreApp] load contratto failed:", e); }
      }
      onLogin(d.access_token, d.user, d.contratto_pendente, contrattoData);
      toast.success(`Benvenuto, ${d.user.nome}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-600 to-indigo-800 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/yollo-logo.png" alt="Yollo" className="mx-auto mb-3 h-16 w-auto brightness-0 invert" />
          <p className="text-indigo-200 text-sm mt-1">Area Commerciale</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-2xl space-y-4">
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Email" autoComplete="email" required data-testid="venditore-email" />
          <div className="relative">
            <Input type={showPwd ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Password" autoComplete="current-password" required data-testid="venditore-password" />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={loading}
            data-testid="venditore-login-btn">
            {loading ? "Accesso..." : "Accedi"}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ═══════════ DASHBOARD ═══════════
function VenditoreDashboard({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/venditore/dashboard`, { headers });
        setData(res.data);
      } catch { toast.error("Errore caricamento"); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-12 text-center text-muted-foreground text-sm">Caricamento...</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="venditore-dashboard">
      {/* KPI Provvigione mensile + annuale */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white border-0">
          <CardContent className="p-4">
            <p className="text-[10px] text-indigo-200 font-medium">Provvigione questo mese</p>
            <p className="text-2xl font-black mt-0.5" style={{color:"#ffffff"}}>&euro; {data.provvigione_mese.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-indigo-200 mt-1">
              {data.diff_clienti >= 0 ? <ArrowUpRight className="h-3 w-3 inline" /> : <ArrowDownRight className="h-3 w-3 inline" />}
              {" "}{data.diff_clienti >= 0 ? "+" : ""}{data.diff_clienti} vs mese scorso
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-violet-600 to-purple-700 text-white border-0">
          <CardContent className="p-4">
            <p className="text-[10px] text-violet-200 font-medium">Totale annuale (proiezione)</p>
            <p className="text-2xl font-black mt-0.5" style={{color:"#ffffff"}}>&euro; {(data.provvigione_annuale || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-violet-200 mt-1">
              {data.mesi_consuntivo || 0} mes{(data.mesi_consuntivo || 0) === 1 ? "e" : "i"} effettiv{(data.mesi_consuntivo || 0) === 1 ? "o" : "i"} + {data.mesi_proiezione || 0} stimat{(data.mesi_proiezione || 0) === 1 ? "o" : "i"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-black text-gray-900">{data.clienti_attivi}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-0.5">Attivi</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-black text-amber-500">{data.lead_in_corso}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-0.5">In prova</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-black text-red-500">{data.clienti_persi || 0}</p>
            <p className="text-[10px] text-muted-foreground font-medium mt-0.5">Persi</p>
          </CardContent>
        </Card>
      </div>

      {/* Grafico andamento provvigioni - curva */}
      {data.storico_provvigioni && data.storico_provvigioni.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-muted-foreground mb-3">Andamento provvigioni</p>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.storico_provvigioni}>
                  <defs>
                    <linearGradient id="provvGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6C63FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6C63FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip
                    formatter={(v) => [`€ ${v.toFixed(2)}`, "Provvigione"]}
                    contentStyle={{ borderRadius: 12, fontSize: 12, border: "1px solid #E5E7EB" }}
                  />
                  <Area type="monotone" dataKey="provvigione" stroke="#6C63FF" strokeWidth={2.5} fill="url(#provvGradient)" dot={{ r: 4, fill: "#6C63FF", strokeWidth: 2, stroke: "#fff" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ultimo cliente acquisito */}
      {data.ultimo_cliente && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <UserPlus className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-gray-900">{data.ultimo_cliente.nome}</p>
              <p className="text-xs text-muted-foreground">Ultimo cliente acquisito</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-emerald-600 font-semibold">{data.ultimo_cliente.data ? new Date(data.ultimo_cliente.data).toLocaleDateString("it-IT") : ""}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert rinnovi */}
      {data.alert_rinnovi.length > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <p className="text-sm font-bold text-red-700">Alert Rinnovi</p>
            </div>
            <p className="text-xs text-red-600 mb-2">{data.alert_rinnovi.length} client{data.alert_rinnovi.length === 1 ? "e" : "i"} da contattare</p>
            {data.alert_rinnovi.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-t border-red-100">
                <span className="text-xs font-medium text-gray-700">{a.nome}</span>
                <span className="text-[10px] text-red-500">scad. {a.scadenza}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Churn mese */}
      {data.churn_mese > 0 && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
              <UserMinus className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{data.churn_mese} client{data.churn_mese === 1 ? "e" : "i"} pers{data.churn_mese === 1 ? "o" : "i"}</p>
              <p className="text-xs text-muted-foreground">Questo mese</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Link referral */}
      {data.link_referral && (
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-muted-foreground mb-1.5">Il tuo link referral</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm font-mono text-indigo-700 truncate">{data.link_referral}</div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`https://${data.link_referral}`); toast.success("Link copiato!"); }}
                data-testid="copy-referral">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Demo Bot Card */}
      <DemoCard />
    </div>
  );
}

// ═══════════ CLIENTI ═══════════
function VenditoreClienti({ headers }) {
  const [clienti, setClienti] = useState([]);
  const [filtro, setFiltro] = useState(null);
  const [search, setSearch] = useState("");
  const [dettaglio, setDettaglio] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchClienti = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/venditore/clienti${filtro ? `?stato=${filtro}` : ""}`;
      const res = await axios.get(url, { headers });
      setClienti(res.data);
    } catch { toast.error("Errore"); }
    setLoading(false);
  }, [filtro]);

  useEffect(() => { fetchClienti(); }, [fetchClienti]);

  const filtered = search ? clienti.filter(c => c.nome.toLowerCase().includes(search.toLowerCase())) : clienti;

  const FILTRI = [
    { key: null, label: "Tutti", count: clienti.length, color: "border-gray-300 bg-white text-gray-700", active: "border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200" },
    { key: "attivi", label: "Attivi", count: clienti.filter(c => c.stato === "attivo").length, color: "border-emerald-300 bg-white text-emerald-700", active: "border-emerald-500 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
    { key: "in_prova", label: "In prova", count: clienti.filter(c => c.stato === "in_prova").length, color: "border-amber-300 bg-white text-amber-700", active: "border-amber-500 bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
    { key: "persi", label: "Persi", count: clienti.filter(c => c.stato === "perso").length, color: "border-red-300 bg-white text-red-700", active: "border-red-500 bg-red-50 text-red-700 ring-1 ring-red-200" },
  ];

  const STATO_BADGE = { attivo: "bg-emerald-500", in_prova: "bg-amber-500", perso: "bg-red-500" };

  return (
    <div className="space-y-3" data-testid="venditore-clienti">
      <h2 className="text-lg font-bold">I miei clienti</h2>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca cliente..."
          className="pl-9 rounded-xl h-11" data-testid="search-clienti" />
      </div>

      {/* Filtri - redesigned */}
      <div className="grid grid-cols-4 gap-2">
        {FILTRI.map(f => (
          <button key={f.key || "all"} onClick={() => setFiltro(f.key)}
            className={`flex flex-col items-center px-2 py-2.5 rounded-xl border-2 text-center transition-all ${filtro === f.key ? f.active : f.color + " opacity-70 hover:opacity-100"}`}
            data-testid={`filtro-${f.key || "tutti"}`}>
            <span className="text-base font-black leading-none">{f.count}</span>
            <span className="text-[10px] font-semibold mt-0.5">{f.label}</span>
          </button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Caricamento...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Nessun cliente trovato</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <Card key={c.subscription_id} className="active:scale-[0.99] transition-transform cursor-pointer"
              onClick={() => setDettaglio(c)} data-testid={`cliente-${c.subscription_id}`}>
              <CardContent className="p-3.5 flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full shrink-0 ${STATO_BADGE[c.stato] || "bg-gray-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{c.nome}</p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {c.pacchetti.map((p, i) => (
                      <span key={i} className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">{p}</span>
                    ))}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-emerald-600">&euro; {c.provvigione_mese}</p>
                  <p className="text-[10px] text-muted-foreground">/mese</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dettaglio modale centrata */}
      {dettaglio && (
        <ClienteDettaglioModal dettaglio={dettaglio} headers={headers} onClose={() => setDettaglio(null)} />
      )}
    </div>
  );
}

function ClienteDettaglioModal({ dettaglio, headers, onClose }) {
  const [full, setFull] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/venditore/clienti/${dettaglio.societa_id}`, { headers });
        setFull(res.data);
      } catch {}
    })();
  }, [dettaglio.societa_id]);

  const data = full || dettaglio;
  const STATO_BADGE = { attivo: "bg-emerald-500", in_prova: "bg-amber-500", perso: "bg-red-500" };
  const STATO_LABEL = { attivo: "Attivo", in_prova: "In prova", perso: "Perso" };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[80vh] overflow-y-auto animate-in fade-in zoom-in-95"
        onClick={e => e.stopPropagation()} data-testid="dettaglio-cliente-sheet">
        <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between z-10 rounded-t-2xl">
          <h3 className="font-bold text-base">{data.nome}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <Row label="Stato"><Badge className={`${STATO_BADGE[data.stato]} text-white text-xs`}>{STATO_LABEL[data.stato] || data.stato}</Badge></Row>

          {/* Telefono proprietario - sempre visibile */}
          {(full?.telefono_proprietario || data.telefono) && (
            <Row label="Cellulare">
              <a href={`tel:${full?.telefono_proprietario || data.telefono}`} className="text-sm font-semibold text-indigo-600 flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                {full?.telefono_proprietario || data.telefono}
              </a>
            </Row>
          )}

          {/* Pacchetti con dettaglio provvigione */}
          <Row label="Pacchetti attivi">
            <div className="space-y-1">
              {(data.pacchetti || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between">
                  <Badge variant="secondary" className="text-xs">{typeof p === "string" ? p : p.nome}</Badge>
                  {typeof p === "object" && p.provvigione > 0 && (
                    <span className="text-[10px] text-emerald-600 font-semibold">&euro; {p.provvigione}/mese</span>
                  )}
                </div>
              ))}
            </div>
          </Row>

          <Row label="Mia provvigione"><span className="font-bold text-emerald-600 text-base">&euro; {data.provvigione_mese}/mese</span></Row>

          {full && (
            <>
              {data.email && <Row label="Email"><span className="text-xs">{data.email}</span></Row>}
              {data.piva && <Row label="P.IVA"><span className="text-xs font-mono">{data.piva}</span></Row>}
              <Row label="Locali / Utenti"><span className="text-xs">{data.num_locali} local{data.num_locali === 1 ? "e" : "i"} — {data.num_utenti} utent{data.num_utenti === 1 ? "e" : "i"}</span></Row>
              {data.data_attivazione && <Row label="Attivazione"><span className="text-xs">{new Date(data.data_attivazione).toLocaleDateString("it-IT")}</span></Row>}
              {data.data_attivazione && (
                <Row label="Scadenza contratto">
                  <span className="text-xs font-semibold flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    {(() => { const d = new Date(data.data_attivazione); d.setFullYear(d.getFullYear() + 1); return d.toLocaleDateString("it-IT"); })()}
                  </span>
                </Row>
              )}
            </>
          )}

          {/* Opportunita upsell */}
          {(data.opportunita || []).length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Opportunita di upsell</p>
              <div className="flex gap-1.5 flex-wrap">
                {data.opportunita.map((o, i) => (
                  <span key={i} className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-lg font-medium border border-emerald-200">
                    + {typeof o === "string" ? o : o.nome}
                    {typeof o === "object" && o.prezzo > 0 && <span className="text-emerald-500 ml-1">&euro;{o.prezzo}/m</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100">
      <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">{label}</span>
      <div className="flex-1 text-sm">{children}</div>
    </div>
  );
}

// ═══════════ PROVVIGIONI ═══════════
function VenditoreProvvigioni({ headers }) {
  const [data, setData] = useState(null);
  const [mese, setMese] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/venditore/provvigioni?mese=${mese}`, { headers });
        setData(res.data);
      } catch { toast.error("Errore"); }
      setLoading(false);
    })();
  }, [mese]);

  // Mesi filtrati dalla data inizio collaborazione
  const mesiOptions = (() => {
    const now = new Date();
    const opts = [];
    let startDate = null;
    if (data?.data_inizio) {
      const [sy, sm] = data.data_inizio.split("-").map(Number);
      if (sy && sm) startDate = new Date(sy, sm - 1, 1);
    }
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      if (startDate && d < startDate) break;
      opts.push({
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString("it-IT", { month: "long", year: "numeric" })
      });
    }
    return opts;
  })();

  return (
    <div className="space-y-4" data-testid="venditore-provvigioni">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Provvigioni</h2>
        <select value={mese} onChange={e => setMese(e.target.value)}
          className="text-xs font-semibold border border-gray-300 rounded-xl px-3 py-2 bg-white text-gray-700 min-w-[160px]" data-testid="mese-select">
          {mesiOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>

      {loading ? <p className="text-sm text-muted-foreground text-center py-8">Caricamento...</p> : data && (
        <>
          {/* Tabella provvigionale - chiudibile */}
          {data.tabella_provvigionale && (data.tabella_provvigionale.pacchetti?.length > 0 || data.tabella_provvigionale.combo?.length > 0 || data.tabella_provvigionale.addon?.length > 0) && (
            <TabellaProvvigionale data={data.tabella_provvigionale} />
          )}

          {/* Totale mese */}
          <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white border-0">
            <CardContent className="p-4">
              <p className="text-xs text-emerald-200 font-medium">Totale mese</p>
              <p className="text-3xl font-black mt-1" style={{color:"#ffffff"}}>&euro; {data.totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-emerald-200 mt-1">{data.num_clienti_attivi} clienti attivi</p>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Dettaglio per cliente</p>
            {data.dettaglio.map((d, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-xl border">
                <span className="text-sm font-medium truncate flex-1">{d.nome}</span>
                {d.gratuito
                  ? <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">In prova</span>
                  : <span className="text-sm font-bold text-emerald-600">&euro; {d.provvigione.toFixed(2)}</span>
                }
              </div>
            ))}
            {data.dettaglio.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nessuna provvigione</p>}
          </div>

          {/* Churn */}
          {data.churn && data.churn.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-red-500">Clienti persi</p>
              {data.churn.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{c.nome}</span>
                    {c.data_perso && <span className="text-[10px] text-muted-foreground">Perso il {new Date(c.data_perso).toLocaleDateString("it-IT")}</span>}
                  </div>
                  <span className="text-sm font-bold text-red-500">- &euro; {c.provvigione_persa.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Export Excel */}
          <Button variant="outline" size="sm" className="w-full"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/api/venditore/provvigioni/export?mese=${mese}`, { headers, responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement("a");
                link.href = url;
                link.download = `provvigioni_${mese}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
                toast.success("Excel scaricato");
              } catch { toast.error("Errore export"); }
            }}
            data-testid="export-provvigioni">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Esporta Excel
          </Button>

          {/* Sezione Pagamenti ricevuti */}
          <PagamentiVenditore headers={headers} />
        </>
      )}
    </div>
  );
}

function PagamentiVenditore({ headers }) {
  const [pagamenti, setPagamenti] = useState([]);
  const [datiYollo, setDatiYollo] = useState({});
  const [loading, setLoading] = useState(true);
  const [fatturaDlg, setFatturaDlg] = useState(null); // pagamento_id
  const [numFattura, setNumFattura] = useState("");
  const [dataFattura, setDataFattura] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/venditore/pagamenti`, { headers });
        setPagamenti(res.data.pagamenti || []);
        setDatiYollo(res.data.dati_fatturazione_yollo || {});
      } catch (e) { console.warn("[VenditoreApp] load pagamenti failed:", e); }
      setLoading(false);
    })();
  }, []);

  const handleSegnaFatturato = async (pagamentoId) => {
    if (!numFattura.trim()) { toast.error("Inserisci il numero fattura"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/venditore/pagamenti/${pagamentoId}/fatturato`, {
        numero_fattura: numFattura, data_fattura: dataFattura || new Date().toISOString().slice(0, 10)
      }, { headers });
      setPagamenti(prev => prev.map(p => p.id === pagamentoId ? { ...p, stato: "fatturato", numero_fattura: numFattura, data_fattura: dataFattura } : p));
      setFatturaDlg(null);
      setNumFattura("");
      setDataFattura("");
      toast.success("Segnato come fatturato");
    } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const handleUpload = async (pagamentoId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/api/venditore/pagamenti/${pagamentoId}/upload-fattura`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      setPagamenti(prev => prev.map(p => p.id === pagamentoId ? { ...p, fattura_url: res.data.fattura_url } : p));
      toast.success("Fattura caricata");
    } catch { toast.error("Errore upload"); }
  };

  if (loading) return null;
  if (pagamenti.length === 0) return null;

  const STATO_STYLE = {
    da_fatturare: "bg-amber-100 text-amber-700",
    fatturato: "bg-emerald-100 text-emerald-700",
  };

  return (
    <div className="space-y-3 pt-2 border-t border-gray-100 mt-4" data-testid="pagamenti-venditore">
      <p className="text-xs font-bold text-muted-foreground flex items-center gap-1.5">
        <CreditCard className="h-3.5 w-3.5" /> Pagamenti ricevuti
      </p>

      {/* Dati fatturazione Yollo */}
      {datiYollo.societa && (
        <div className="bg-gray-50 rounded-xl p-3 text-[11px] text-gray-500 space-y-0.5">
          <p className="font-semibold text-gray-700 text-xs mb-1">Dati per la fatturazione</p>
          <p>{datiYollo.societa}</p>
          {datiYollo.sede_legale && <p>{datiYollo.sede_legale}{datiYollo.citta ? `, ${datiYollo.citta}` : ""}</p>}
          {datiYollo.partita_iva && <p>P.IVA: {datiYollo.partita_iva}</p>}
          {datiYollo.codice_univoco && <p>SDI: {datiYollo.codice_univoco}</p>}
          {datiYollo.pec && <p>PEC: {datiYollo.pec}</p>}
        </div>
      )}

      {pagamenti.map(p => (
        <Card key={p.id}>
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-bold text-gray-900">&euro; {p.importo.toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground">{p.periodo} &middot; {p.metodo_pagamento}</p>
              </div>
              <Badge className={`text-[10px] ${STATO_STYLE[p.stato] || "bg-gray-100 text-gray-500"}`}>
                {p.stato === "da_fatturare" ? "Da fatturare" : "Fatturato"}
              </Badge>
            </div>

            {p.numero_fattura && (
              <p className="text-[10px] text-gray-500">Fatt. n. {p.numero_fattura} del {p.data_fattura}</p>
            )}

            <div className="flex gap-2 mt-2">
              {p.stato === "da_fatturare" && (
                <Button size="sm" variant="outline" className="text-[11px] h-7 rounded-lg flex-1"
                  onClick={() => { setFatturaDlg(p.id); setNumFattura(""); setDataFattura(new Date().toISOString().slice(0, 10)); }}
                  data-testid={`segna-fatturato-${p.id}`}>
                  <FileText className="h-3 w-3 mr-1" /> Segna fatturato
                </Button>
              )}
              {!p.fattura_url && (
                <label className="flex-1">
                  <input type="file" accept=".pdf,.png,.jpg" className="hidden"
                    onChange={e => e.target.files[0] && handleUpload(p.id, e.target.files[0])} />
                  <Button size="sm" variant="outline" className="text-[11px] h-7 rounded-lg w-full pointer-events-none" tabIndex={-1}>
                    <Download className="h-3 w-3 mr-1 rotate-180" /> Carica fattura
                  </Button>
                </label>
              )}
              {p.fattura_url && (
                <a href={p.fattura_url.startsWith("http") ? p.fattura_url : `${API.replace("/api","")}${p.fattura_url}`} target="_blank" rel="noreferrer"
                  className="text-[11px] text-indigo-600 font-semibold flex items-center gap-1 hover:underline">
                  <Download className="h-3 w-3" /> Scarica fattura
                </a>
              )}
            </div>

            {/* Mini dialog inline per segnare fatturato */}
            {fatturaDlg === p.id && (
              <div className="mt-3 p-3 bg-gray-50 rounded-xl space-y-2 border border-gray-200">
                <Input value={numFattura} onChange={e => setNumFattura(e.target.value)} placeholder="Numero fattura" className="h-8 text-sm" data-testid="input-num-fattura" />
                <DateInput value={dataFattura} onChange={(v) => setDataFattura(v)} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleSegnaFatturato(p.id)} disabled={saving} className="flex-1 h-7 text-[11px]">
                    {saving ? "..." : "Conferma"}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setFatturaDlg(null)}>Annulla</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


function TabellaProvvigionale({ data }) {
  const [open, setOpen] = useState(true);

  const ROWS = [
    { key: "pacchetti", label: "Pacchetti", items: data.pacchetti || [], bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-600", icon: "Package" },
    { key: "combo", label: "Bundle", items: data.combo || [], bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-600", icon: "Layers" },
    { key: "addon", label: "Servizi", items: data.addon || [], bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", icon: "Settings" },
  ];

  const hasData = ROWS.some(r => r.items.length > 0);
  if (!hasData) return null;

  return (
    <Card>
      <CardContent className="p-0">
        <button onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          data-testid="toggle-tabella-provvigionale">
          <span className="text-xs font-bold text-muted-foreground flex items-center gap-1.5">
            <Percent className="h-3.5 w-3.5" /> La mia tabella provvigionale
          </span>
          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
        </button>
        {open && (
          <div className="px-3 pb-4 space-y-3">
            {ROWS.map(row => row.items.length > 0 && (
              <div key={row.key} className="grid grid-cols-3 gap-2">
                {row.items.map((t, i) => (
                  <div key={i} className={`${row.bg} ${row.border} border rounded-xl p-2.5 flex flex-col items-center text-center`}>
                    <p className={`text-[10px] font-semibold ${row.text} leading-tight`}>{t.voce}</p>
                    <p className={`text-sm font-black ${row.text} mt-1`}>
                      {t.tipo === "percentuale" ? `${t.valore}%` : `${t.valore}€`}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════ PROFILO ═══════════
function VenditoreProfilo({ headers }) {
  const [profilo, setProfilo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/venditore/profilo`, { headers });
        setProfilo(res.data);
        setForm(res.data);
      } catch { toast.error("Errore"); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      ["nome","cognome","telefono","codice_fiscale","partita_iva","indirizzo","cap","citta","provincia","data_nascita","luogo_nascita","tipo_documento","numero_documento","iban","intestatario_conto","banca","pec_cod_univoco","note"].forEach(k => { payload[k] = form[k] || ""; });
      await axios.put(`${API}/api/venditore/profilo`, payload, { headers });
      toast.success("Profilo aggiornato");
      setProfilo({ ...profilo, ...form });
      setEditing(false);
    } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const F = (k, v) => setForm({ ...form, [k]: v });

  if (!profilo) return <p className="text-sm text-muted-foreground text-center py-8">Caricamento...</p>;

  return (
    <div className="space-y-4 pb-4" data-testid="venditore-profilo">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Il mio profilo</h2>
        {!editing ? (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-xl" data-testid="edit-profilo">Modifica</Button>
        ) : (
          <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-xl" data-testid="save-profilo">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Salvo..." : "Salva"}
          </Button>
        )}
      </div>

      {/* Codice Venditore */}
      <Card className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white border-0">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-indigo-200 font-medium">Il tuo codice venditore</p>
            <p className="text-2xl font-black tracking-wider mt-0.5" style={{color:"#ffffff"}}>{profilo.codice_venditore}</p>
          </div>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/20"
            onClick={() => { navigator.clipboard.writeText(profilo.codice_venditore); toast.success("Codice copiato!"); }}>
            <Copy className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      {/* Dati Anagrafici / Societa */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-sm font-bold flex items-center gap-1.5"><Building2 className="h-4 w-4 text-muted-foreground" /> Dati Anagrafici</p>
          {editing ? (
            <div className="space-y-2.5">
              <Input value={form.partita_iva || ""} onChange={e => F("partita_iva", e.target.value)} placeholder="P.IVA / Cod. Fiscale" />
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.nome || ""} onChange={e => F("nome", e.target.value)} placeholder="Nome" />
                <Input value={form.cognome || ""} onChange={e => F("cognome", e.target.value)} placeholder="Cognome" />
              </div>
              <Input value={form.telefono || ""} onChange={e => F("telefono", e.target.value)} placeholder="Telefono" />
              <Input value={form.citta || ""} onChange={e => F("citta", e.target.value)} placeholder="Citta" />
            </div>
          ) : (
            <div className="space-y-1.5">
              <PRow label="P.IVA / C.F.">{profilo.partita_iva || profilo.codice_fiscale || "-"}</PRow>
              <PRow label="Nome">{profilo.nome} {profilo.cognome}</PRow>
              <PRow label="Telefono">{profilo.telefono || "-"}</PRow>
              <PRow label="Email">{profilo.email}</PRow>
              <PRow label="Citta">{profilo.citta || "-"}</PRow>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dati Pagamento */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-sm font-bold flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-muted-foreground" /> Dati del Pagamento</p>
          {editing ? (
            <div className="space-y-2.5">
              <Input value={form.intestatario_conto || ""} onChange={e => F("intestatario_conto", e.target.value)} placeholder="Intestatario Conto" />
              <Input value={form.banca || ""} onChange={e => F("banca", e.target.value)} placeholder="Banca per accredito" />
              <Input value={form.iban || ""} onChange={e => F("iban", e.target.value)} placeholder="IBAN" />
              <Input value={form.pec_cod_univoco || ""} onChange={e => F("pec_cod_univoco", e.target.value)} placeholder="PEC / Cod. Univoco" />
            </div>
          ) : (
            <div className="space-y-1.5">
              <PRow label="Intestatario">{profilo.intestatario_conto || <span className="italic text-muted-foreground">Non inserito</span>}</PRow>
              <PRow label="Banca">{profilo.banca || <span className="italic text-muted-foreground">Non inserita</span>}</PRow>
              <PRow label="IBAN">{profilo.iban || <span className="italic text-muted-foreground">Non inserito</span>}</PRow>
              <PRow label="PEC / SDI">{profilo.pec_cod_univoco || <span className="italic text-muted-foreground">Non inserito</span>}</PRow>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Link referral */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-bold mb-2">Il mio link referral</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-indigo-50 rounded-lg px-3 py-2 text-sm font-mono text-indigo-700 truncate">{profilo.link_referral}</div>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`https://${profilo.link_referral}`); toast.success("Copiato!"); }}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground"><b>{profilo.referral_count}</b> registrazioni tramite il tuo link</span>
          </div>
        </CardContent>
      </Card>

      {/* Dati di Accesso */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-sm font-bold flex items-center gap-1.5"><KeyRound className="h-4 w-4 text-muted-foreground" /> Dati di Accesso</p>
          <PRow label="Username">{profilo.email}</PRow>
          <CambiaPasswordInline headers={headers} />
        </CardContent>
      </Card>
    </div>
  );
}

function CambiaPasswordInline({ headers }) {
  const [show, setShow] = useState(false);
  const [pwdAttuale, setPwdAttuale] = useState("");
  const [pwdNuova, setPwdNuova] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCambiaPassword = async () => {
    if (!pwdNuova || pwdNuova.length < 4) { toast.error("La password deve essere di almeno 4 caratteri"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/venditore/profilo/password`, { password_attuale: pwdAttuale, nuova_password: pwdNuova }, { headers });
      toast.success("Password aggiornata");
      setShow(false);
      setPwdAttuale("");
      setPwdNuova("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  return (
    <div>
      {!show ? (
        <div className="flex items-start gap-3 py-1">
          <span className="text-[11px] text-muted-foreground w-24 shrink-0">Password</span>
          <button onClick={() => setShow(true)} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="cambia-password-btn">
            Cambia password
          </button>
        </div>
      ) : (
        <div className="space-y-2 pt-1 border-t border-gray-100 mt-1">
          <Input type="password" value={pwdAttuale} onChange={e => setPwdAttuale(e.target.value)} placeholder="Password attuale" className="h-9 text-sm" />
          <Input type="password" value={pwdNuova} onChange={e => setPwdNuova(e.target.value)} placeholder="Nuova password" className="h-9 text-sm" />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCambiaPassword} disabled={saving} className="flex-1 h-8 text-xs" data-testid="conferma-password-btn">
              {saving ? "Salvo..." : "Conferma"}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setShow(false); setPwdAttuale(""); setPwdNuova(""); }}>Annulla</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PRow({ label, children }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <span className="text-[11px] text-muted-foreground w-24 shrink-0">{label}</span>
      <div className="flex-1 text-sm">{children}</div>
    </div>
  );
}
